import React from 'react';
import './DestinationGlobe.css';

/**
 * Animated SVG globe — the centerpiece of the Destination Success theme.
 * Pure SVG + CSS keyframes (no Three.js / WebGL) so it renders crisply on
 * mobile and stays under ~6KB inline.
 *
 * Composition:
 *  - meridians + parallels grid (rotates as the globe spins)
 *  - dot-matrix continents (simplified, evocative, not literal)
 *  - terminator gradient — illuminated horizon-gold side, midnight shadow side
 *  - pulsing destination markers at named cities
 *  - orbiting jet on an outer ring (counter-spinning so it always appears upright)
 */
export default function DestinationGlobe({ size = 360, className = '' }) {
  return (
    <div
      className={`destination-globe ${className}`}
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      <svg
        viewBox="0 0 400 400"
        width={size}
        height={size}
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          {/* The lit side of the sphere */}
          <radialGradient id="globeShade" cx="35%" cy="32%" r="80%">
            <stop offset="0%"  stopColor="#244177" />
            <stop offset="55%" stopColor="#162a55" />
            <stop offset="100%" stopColor="#080f24" />
          </radialGradient>

          {/* Sunrise rim light */}
          <radialGradient id="rimLight" cx="32%" cy="28%" r="42%">
            <stop offset="0%"  stopColor="rgba(244, 162, 97, 0.55)" />
            <stop offset="60%" stopColor="rgba(244, 162, 97, 0.05)" />
            <stop offset="100%" stopColor="rgba(244, 162, 97, 0)" />
          </radialGradient>

          {/* Soft outer atmosphere */}
          <radialGradient id="atmosphere" cx="50%" cy="50%" r="50%">
            <stop offset="78%" stopColor="rgba(94, 234, 212, 0)" />
            <stop offset="92%" stopColor="rgba(94, 234, 212, 0.18)" />
            <stop offset="100%" stopColor="rgba(94, 234, 212, 0)" />
          </radialGradient>

          {/* Mint glow filter for flight paths and markers */}
          <filter id="mintGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          <clipPath id="sphereClip">
            <circle cx="200" cy="200" r="150" />
          </clipPath>
        </defs>

        {/* atmosphere halo */}
        <circle cx="200" cy="200" r="180" fill="url(#atmosphere)" />

        {/* sphere base */}
        <circle cx="200" cy="200" r="150" fill="url(#globeShade)" />

        {/* rotating layer — grid + continents */}
        <g className="globe-rotate" clipPath="url(#sphereClip)">
          {/* parallels */}
          <g stroke="rgba(254, 252, 247, 0.10)" strokeWidth="1" fill="none">
            <ellipse cx="200" cy="200" rx="150" ry="22" />
            <ellipse cx="200" cy="200" rx="150" ry="60" />
            <ellipse cx="200" cy="200" rx="150" ry="100" />
            <ellipse cx="200" cy="200" rx="150" ry="135" />
          </g>
          {/* meridians */}
          <g stroke="rgba(254, 252, 247, 0.10)" strokeWidth="1" fill="none">
            <ellipse cx="200" cy="200" rx="22" ry="150" />
            <ellipse cx="200" cy="200" rx="60" ry="150" />
            <ellipse cx="200" cy="200" rx="100" ry="150" />
            <ellipse cx="200" cy="200" rx="135" ry="150" />
          </g>

          {/* equator highlight */}
          <line x1="50" y1="200" x2="350" y2="200" stroke="rgba(244, 162, 97, 0.22)" strokeWidth="1" />

          {/* dot-matrix continents (simplified) */}
          <g fill="rgba(254, 252, 247, 0.78)">
            {CONTINENT_DOTS.map(([cx, cy, r], i) => (
              <circle key={i} cx={cx} cy={cy} r={r} />
            ))}
          </g>
        </g>

        {/* terminator / sunrise rim light (does NOT rotate — it's the camera light) */}
        <circle cx="200" cy="200" r="150" fill="url(#rimLight)" />

        {/* pulsing destination markers (do not rotate — fixed cities of interest) */}
        <g className="destination-markers">
          {[
            { cx: 188, cy: 135, label: 'A' },
            { cx: 265, cy: 215, label: 'B' },
            { cx: 152, cy: 248, label: 'C' },
            { cx: 240, cy: 162, label: 'D' },
          ].map((m, i) => (
            <g key={i} style={{ animationDelay: `${i * 0.6}s` }} className="marker">
              <circle cx={m.cx} cy={m.cy} r="3" fill="var(--coral)" filter="url(#mintGlow)" />
              <circle cx={m.cx} cy={m.cy} r="3" fill="var(--coral)" className="marker-pulse" />
            </g>
          ))}
        </g>

        {/* flight-path arc connecting two markers */}
        <path
          d="M 188 135 Q 280 110 265 215"
          fill="none"
          stroke="var(--mint)"
          strokeWidth="1.5"
          strokeDasharray="4 4"
          strokeLinecap="round"
          className="flight-path-draw"
          filter="url(#mintGlow)"
        />

        {/* orbiting jet on an outer ring */}
        <g className="jet-orbit" style={{ transformOrigin: '200px 200px' }}>
          <g transform="translate(200, 30)">
            <g transform="rotate(90)">
              {/* simplified jet silhouette */}
              <path
                d="M -8 0 L 8 0 M 0 -10 L 0 10 M -3 -3 L -10 -8 M 3 -3 L 10 -8"
                stroke="var(--cloud)"
                strokeWidth="1.6"
                strokeLinecap="round"
                fill="none"
              />
              <circle cx="0" cy="0" r="2" fill="var(--cloud)" />
            </g>
          </g>
        </g>
      </svg>
    </div>
  );
}

/* Stylized continent dot field. Coordinates are within the 400x400 viewBox,
   centered on (200,200) with sphere radius 150. */
const CONTINENT_DOTS = [
  // North America
  [125, 150, 2.4], [135, 155, 1.8], [145, 142, 2.2], [120, 165, 2],
  [140, 170, 1.6], [155, 158, 1.8], [115, 178, 1.6], [132, 185, 2],
  // South America
  [165, 230, 2], [172, 245, 2.2], [168, 260, 1.8], [178, 270, 1.6],
  // Europe
  [205, 145, 2], [218, 152, 1.6], [212, 160, 1.8],
  // Africa
  [215, 198, 2.2], [222, 215, 2], [218, 232, 1.8], [228, 248, 1.6],
  // Middle East / Asia
  [240, 168, 2], [255, 175, 1.8], [268, 165, 2.2], [280, 178, 1.8],
  // South / SE Asia
  [262, 200, 1.6], [275, 210, 2], [285, 222, 1.8],
  // East Asia
  [290, 175, 2], [300, 165, 1.6],
  // Australia
  [285, 258, 2.2], [298, 265, 1.8],
];
